import { useState, Suspense } from 'react';
import { motion } from 'framer-motion';
import { XCircle, RefreshCw } from 'lucide-react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import { Badge } from '@lecartte/shared-ui/components/Badge';
import type { RejectedAccountsPageQuery } from '@/__generated__/RejectedAccountsPageQuery.graphql';

const QUERY = graphql`
  query RejectedAccountsPageQuery {
    adminRejectedAccounts {
      id
      name
      email
      restaurantName
      restaurantSlug
      rejectedAt
    }
  }
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function RejectedAccountsContent({ fetchKey }: { fetchKey: number }) {
  const data = useLazyLoadQuery<RejectedAccountsPageQuery>(
    QUERY,
    {},
    { fetchKey, fetchPolicy: 'network-only' }
  );

  const accounts = data.adminRejectedAccounts;

  if (accounts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
          <XCircle size={24} className="text-slate-400" />
        </div>
        <p className="font-semibold text-slate-700">Nenhuma conta rejeitada</p>
        <p className="text-sm text-slate-400 mt-1">Contas rejeitadas aparecerão aqui.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-500">
        {accounts.length} {accounts.length === 1 ? 'conta rejeitada' : 'contas rejeitadas'}
      </p>
      {accounts.map((account) => (
        <motion.div
          key={account.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-800 truncate">{account.name}</h3>
              <Badge label="Rejeitada" variant="danger" />
            </div>
            <p className="text-sm text-slate-500 truncate">{account.email}</p>
            {account.restaurantName && (
              <p className="text-xs text-slate-400 mt-0.5">
                Restaurante:{' '}
                <span className="font-medium text-slate-600">{account.restaurantName}</span>
                {account.restaurantSlug && (
                  <span className="text-slate-400"> · @{account.restaurantSlug}</span>
                )}
              </p>
            )}
            {account.rejectedAt && (
              <p className="text-xs text-red-400 mt-1">
                Rejeitada em {formatDate(account.rejectedAt)}
              </p>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

export function RejectedAccountsPage() {
  const [fetchKey, setFetchKey] = useState(0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setFetchKey((k) => k + 1)}
        >
          <RefreshCw size={14} />
          Atualizar
        </Button>
      </div>
      <Suspense
        fallback={
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
            Carregando...
          </div>
        }
      >
        <RejectedAccountsContent fetchKey={fetchKey} />
      </Suspense>
    </div>
  );
}
